import React from 'react';
import { Menu, MenuItem, MenuItemProps, Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faEllipsisH } from '@fortawesome/pro-light-svg-icons';
import { Dimensions, ColorPalette } from '../config';

export interface IFloatingActionButtonMenuProps {
  menuItems: IFloatingActionButtonMenuItemProps[];
  anchorEl: null | HTMLElement;
  handleClose: () => void;
}
export interface IFloatingActionButtonMenuItemProps {
  label: string;
  url?: string;
  disabled?: boolean;
  icon?: IconDefinition;
  onMenuClick?: (target: IFloatingActionButtonMenuItemProps) => void;
}

export interface IFloatingActionButtonMenuContainer {
  menuItems: MenuItemProps[];
  handleClose: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  anchorEl: null | HTMLElement;
}

export function FloatingActionButtonMenu(props: IFloatingActionButtonMenuProps) {
  const { menuItems, handleClose, anchorEl } = props;
  const handleClick = (target: IFloatingActionButtonMenuItemProps) => {
    if (target.onMenuClick) {
      target.onMenuClick(target);
    }
    handleClose();
  };

  const renderMenuItems: MenuItemProps[] = menuItems.map((item, i) => (
    <MenuItem disabled={item.disabled} key={`fab_item_${i}`} dense onClick={() => handleClick(item)} sx={{ display: 'flex', px: 1 }}>
      {item.icon && (
        <Box sx={{ display: 'inline-block', width: Dimensions.ThemeSpacingUnit * 4, flex: 0, mr: 1 }}>
          <FontAwesomeIcon icon={item.icon} />
        </Box>
      )}
      {item.label}
    </MenuItem>
  ));

  return <FloatingActionMenuButtonMenuContainer {...props} handleClose={handleClose} anchorEl={anchorEl} menuItems={renderMenuItems} />;
}

export function FloatingActionMenuButtonMenuContainer(props: IFloatingActionButtonMenuContainer) {
  const { menuItems, anchorEl, handleClose } = props;
  const menuId = 'button-menu';
  return (
    <>
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          backgroundColor: ColorPalette.WHITE,
          borderRadius: '4px',
          padding: '2px',
          display: 'block',
          top: 35,
          right: 22,
          boxShadow: '1px 1px 3px rgba(0,0,0,.1)',
          overflow: 'hidden',
          height: 10,
          '& svg': {
            color: ColorPalette.DefaultTextColor,
            transform: 'translateY(-9px)',
          },
        }}
      >
        <FontAwesomeIcon icon={faEllipsisH} />
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: -2.5,
            ml: 2.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 10,
              right: -3,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        MenuListProps={{
          sx: {
            py: 0,
          },
        }}
      >
        {menuItems as React.ReactNode[]}
      </Menu>
    </>
  );
}
